import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { motion } from 'framer-motion';
import { Brain } from 'lucide-react';

// Utility to shuffle an array
const shuffle = arr => arr.sort(() => Math.random() - 0.5);

// Vocabulary question bank by difficulty
const questionBank = {
  easy: [
    { word: 'Happy', options: ['Joyful','Sad','Angry','Tired'], answer: 'Joyful' },
    { word: 'Large', options: ['Big','Small','Tiny','Short'], answer: 'Big' },
    { word: 'Bright', options: ['Dark','Shiny','Dull','Heavy'], answer: 'Shiny' },
    { word: 'Quick', options: ['Fast','Slow','Steady','Late'], answer: 'Fast' },
    { word: 'Quiet', options: ['Silent','Loud','Brave','Cold'], answer: 'Silent' },
    { word: 'Warm', options: ['Hot','Cold','Wet','Dry'], answer: 'Hot' },
    { word: 'Smooth', options: ['Rough','Soft','Hard','Sharp'], answer: 'Soft' },
    { word: 'Tiny', options: ['Small','Large','Medium','Huge'], answer: 'Small' }
  ],
  medium: [
    { word: 'Reluctant', options: ['Willing','Hesitant','Eager','Quick'], answer: 'Hesitant' },
    { word: 'Obsolete', options: ['Modern','Outdated','Useful','Popular'], answer: 'Outdated' },
    { word: 'Candid', options: ['Honest','Secretive','Angry','Brave'], answer: 'Honest' },
    { word: 'Prudent', options: ['Careless','Wise','Slow','Rude'], answer: 'Wise' },
    { word: 'Mediocre', options: ['Excellent','Average','Terrible','Unique'], answer: 'Average' },
    { word: 'Astute', options: ['Dull','Shrewd','Angry','Loud'], answer: 'Shrewd' },
    { word: 'Serene', options: ['Calm','Loud','Angry','Sad'], answer: 'Calm' },
    { word: 'Tumult', options: ['Chaos','Peace','Silence','Order'], answer: 'Chaos' },
    { word: 'Vivid', options: ['Dull','Bright','Dark','Faint'], answer: 'Bright' },
    { word: 'Dismal', options: ['Cheerful','Gloomy','Bright','Sunny'], answer: 'Gloomy' },
    { word: 'Eager', options: ['Reluctant','Enthusiastic','Indifferent','Slow'], answer: 'Enthusiastic' },
    { word: 'Frugal', options: ['Wasteful','Thrifty','Generous','Lavish'], answer: 'Thrifty' },
    { word: 'Agile', options: ['Clumsy','Quick','Slow','Heavy'], answer: 'Quick' },
    { word: 'Robust', options: ['Weak','Strong','Fragile','Tiny'], answer: 'Strong' },
    { word: 'Lucid', options: ['Clear','Confusing','Dark','Blurry'], answer: 'Clear' },
    { word: 'Timid', options: ['Bold','Shy','Brave','Loud'], answer: 'Shy' }
  ],
  hard: [
    { word: 'Ephemeral', options: ['Lifelong','Short-lived','Permanent','Endless'], answer: 'Short-lived' },
    { word: 'Audacious', options: ['Timid','Bold','Meek','Quiet'], answer: 'Bold' },
    { word: 'Ubiquitous', options: ['Rare','Widespread','Hidden','Limited'], answer: 'Widespread' },
    { word: 'Loquacious', options: ['Silent','Talkative','Shy','Calm'], answer: 'Talkative' },
    { word: 'Obfuscate', options: ['Clarify','Confuse','Cleanse','Heal'], answer: 'Confuse' },
    { word: 'Pernicious', options: ['Harmless','Destructive','Friendly','Simple'], answer: 'Destructive' },
    { word: 'Perseverance', options: ['Persistence','Failure','Quitting','Rest'], answer: 'Persistence' },
    { word: 'Quintessential', options: ['Representative','Unusual','Mediocre','Rare'], answer: 'Representative' },
    { word: 'Transcend', options: ['Surpass','Fall','Descend','Ignore'], answer: 'Surpass' },
    { word: 'Obstreperous', options: ['Quiet','Noisy','Silent','Shy'], answer: 'Noisy' },
    { word: 'Zephyr', options: ['Breeze','Storm','Heat','Rain'], answer: 'Breeze' },
    { word: 'Idiosyncrasy', options: ['Eccentricity','Similarity','Normality','Banality'], answer: 'Eccentricity' },
    { word: 'Synergy', options: ['Cooperation','Conflict','War','Argue'], answer: 'Cooperation' },
    { word: 'Convalesce', options: ['Recover','Weaken','Fall','Break'], answer: 'Recover' },
    { word: 'Epistemology', options: ['Knowledge','Ignorance','History','Biology'], answer: 'Knowledge' },
    { word: 'Recalcitrant', options: ['Obedient','Stubborn','Compliant','Gentle'], answer: 'Stubborn' }
  ]
};

// Memory test word bank by difficulty
const memoryBank = {
  easy: ['Apple','Ball','Cat','Dog','Fish','Guitar','House','Ice'],
  medium: ['Elephant','Flower','Guitar','Helmet','Island','Jacket'],
  hard: ['Hippopotamus','Jellyfish','Kangaroo','Lollipop','Microwave','Nightmare']
};

export default function BrainTestPage() {
    // PDF download handler using html2canvas & jsPDF
    const handleDownloadPDF = () => {
      import('html2canvas').then(html2canvas => {
        import('jspdf').then(jsPDF => {
          const doc = new jsPDF.jsPDF({ orientation: 'portrait', unit: 'px' });
          html2canvas.default(document.getElementById('report-card')).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const imgProps = doc.getImageProperties(imgData);
            const pdfWidth = doc.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            doc.save('brain-test-report.pdf');
          });
        });
      });
    };
    
  // Participant info
  const [childName, setChildName] = useState('');
  const [parentName, setParentName] = useState('');
  const [contact, setContact] = useState('');
  const [age, setAge] = useState('');

  // Flow control
  const [phase, setPhase] = useState('info'); // info, select, vocab, memory, results
  const [testType, setTestType] = useState(null); // 'vocab' or 'memory'

  // Vocab states
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);

  // Memory states
  const [sequence, setSequence] = useState([]);
  const [colorSequence, setColorSequence] = useState([]);
  const [showColors, setShowColors] = useState(false);
  const [userColorInput, setUserColorInput] = useState([]);
  const colorBank = ['🔴','🟢','🔵','🟡','🟠','🟣'];
  const [colorLevel, setColorLevel] = useState(6);
  const [showSequence, setShowSequence] = useState(false);
  const [revealIndex, setRevealIndex] = useState(0);
  const [userInputs, setUserInputs] = useState([]);

  // After info
  const handleInfoSubmit = () => setPhase('select');

  // Start Vocabulary Test
  const startVocab = () => {
    const easyQs = shuffle(questionBank.easy).slice(0,4).map(q => ({ ...q, difficulty: 'Easy' }));
    const medQs = shuffle(questionBank.medium).slice(0,3).map(q => ({ ...q, difficulty: 'Medium' }));
    const hardQs = shuffle(questionBank.hard).slice(0,3).map(q => ({ ...q, difficulty: 'Hard' }));
    setQuestions([...easyQs, ...medQs, ...hardQs]);
    setTestType('vocab'); setPhase('vocab'); setCurrentIndex(0); setScore(0);
                    setColorLevel(6);
  };

  // Start Memory Test
  const startMemory = () => {
    const easy = shuffle(memoryBank.easy).slice(0,4);
    const med = shuffle(memoryBank.medium).slice(0,3);
    const hard = shuffle(memoryBank.hard).slice(0,3);
    const seq = [...easy, ...med, ...hard];
    setSequence(seq);
    setUserInputs(Array(seq.length).fill(''));
    setTestType('memory'); setPhase('memory'); setShowSequence(true);
    setRevealIndex(0); setScore(0);
  };

  // Sequential reveal (5s per word)
  useEffect(() => {
    let timer;
    if (phase==='memory' && showSequence) {
      if (revealIndex < sequence.length - 1) {
        timer = setTimeout(() => setRevealIndex(idx => idx + 1), 5000);
      } else {
        timer = setTimeout(() => setShowSequence(false), 5000);
      }
    }
    return () => clearTimeout(timer);
  }, [phase, showSequence, revealIndex, sequence]);

  useEffect(() => {
    let timer;
    if (phase === 'color' && showColors) {
      if (userColorInput.length < colorSequence.length - 1) {
        timer = setTimeout(() => {
          setUserColorInput(inputs => [...inputs, null]);
        }, 3000);
      } else {
        timer = setTimeout(() => {
          setShowColors(false);
          setUserColorInput([]);
        }, 3000);
      }
    }
    return () => clearTimeout(timer);
  }, [phase, showColors, userColorInput, colorSequence]);

  // Handle vocab click
  const handleVocabClick = opt => {
    if (opt === questions[currentIndex].answer) {setScore(s=>s+1);}
    if (currentIndex+1 < questions.length) { setCurrentIndex(i=>i+1);}
    else if (testType === 'color' && score === colorSequence.length) {
  setColorLevel(prev => Math.min(prev + 1, colorBank.length));
}else{
setPhase('results');}
  };

  // Handle memory input change
  const handleInputChange = (idx, val) => {
    setUserInputs(inputs => inputs.map((v,i)=>i===idx?val:v));
  };

  // Submit memory sequence
  const handleMemorySubmit = () => {
    let s=0;
    userInputs.forEach((inp,i)=>{
      if (inp.trim().toLowerCase()===sequence[i].toLowerCase()) s++;
    });
    setScore(s);
    setPhase('results');
  };

  const totalItems = testType === 'memory' 
  ? sequence.length 
  : testType === 'color' 
    ? colorSequence.length 
    : questions.length;
  const percentScore = totalItems > 0 ? Math.round((score / totalItems) * 100) : 0;
  const chartData = [{name:'Brainies',score:90},{name:'You',score:percentScore}];

  // Badge colors
  const diffColor = {Easy:'bg-green-100 text-green-800',Medium:'bg-yellow-100 text-yellow-800',Hard:'bg-red-100 text-red-800'};

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-8 space-y-8">
      {/* Info Phase */}
      {phase==='info'&&(
        <motion.div initial={{opacity:0}} animate={{opacity:1}}>
          <Card className="p-8 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-2xl shadow-2xl relative">
            <Brain className="absolute top-4 right-4 text-6xl opacity-20"/>
            <h2 className="text-3xl mb-6 text-center font-bold">Basic Information</h2>
            <Input placeholder="Child's Name" value={childName} onChange={e=>setChildName(e.target.value)} className="mb-4 bg-white text-black rounded-lg"/>
            <Input placeholder="Parent's Name" value={parentName} onChange={e=>setParentName(e.target.value)} className="mb-4 bg-white text-black rounded-lg"/>
            <Input placeholder="Contact" value={contact} onChange={e=>setContact(e.target.value)} className="mb-4 bg-white text-black rounded-lg"/>
            <Input type="number" placeholder="Child's Age" value={age} onChange={e=>setAge(e.target.value)} className="mb-6 bg-white text-black rounded-lg"/>
            <Button size="md" className="w-full bg-white text-purple-600 rounded-full py-2" disabled={!childName||!parentName||!contact||!age} onClick={handleInfoSubmit}>Proceed</Button>
          </Card>
        </motion.div>
      )}

      {/* Selection Phase */}
      {phase==='select'&&(
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
          className="flex flex-col items-center space-y-8"
        >
          <h2 className="text-2xl font-bold text-gray-700">Choose Your Test</h2>
          <div className="flex flex-wrap justify-center gap-6">
            <Card className="w-64 p-6 bg-white shadow-lg hover:shadow-2xl rounded-xl cursor-pointer transition-transform transform hover:-translate-y-1"
                  onClick={() => {
                    const seq = shuffle(colorBank).slice(0, 6);
                    setColorSequence(seq);
                    setUserColorInput([]);
                    setTestType('color');
                    setPhase('color');
                    setShowColors(true);
                    setScore(0);
                  }}>
              <h3 className="text-xl font-semibold mb-2 text-pink-600">Color Memory</h3>
              <p className="text-gray-500 text-sm">Repeat the color pattern in order.</p>
            </Card>
            <Card className="w-64 p-6 bg-white shadow-lg hover:shadow-2xl rounded-xl cursor-pointer transition-transform transform hover:-translate-y-1"
                  onClick={startVocab}>
              <h3 className="text-xl font-semibold mb-2 text-green-600">Vocabulary Test</h3>
              <p className="text-gray-500 text-sm">Assess your word knowledge.</p>
            </Card>
            <Card className="w-64 p-6 bg-white shadow-lg hover:shadow-2xl rounded-xl cursor-pointer transition-transform transform hover:-translate-y-1"
                  onClick={startMemory}>
              <h3 className="text-xl font-semibold mb-2 text-blue-600">Memory Test</h3>
              <p className="text-gray-500 text-sm">Recall sequences of words.</p>
            </Card>
          </div>
        </motion.div>
      )}

      {/* Vocabulary Test Phase */}
      {phase==='vocab'&& testType==='vocab'&&(
        <Card className="p-8 bg-white rounded-2xl shadow-xl border-4 border-purple-300">
          <div className="mb-6">
            <div className="h-4 bg-purple-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-purple-500"
                style={{ width: `${((currentIndex)/questions.length)*100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-right text-sm mt-1 text-gray-600">Question {currentIndex+1} of {questions.length}</p>
          </div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-3xl font-bold text-purple-600">{questions[currentIndex].word}</h3>
            <span className={`px-4 py-2 text-base font-semibold rounded-full ${diffColor[questions[currentIndex].difficulty]}`}>{questions[currentIndex].difficulty}</span>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {questions[currentIndex].options.map(opt => (
              <motion.div whileTap={{ scale: 0.95 }} key={opt}>
                <Button
                  variant="default"
                  size="lg"
                  className="w-full text-lg bg-gradient-to-r from-purple-200 to-purple-100 text-purple-700 border-2 border-purple-300 hover:from-purple-300 hover:to-purple-200 hover:text-purple-900 rounded-xl"
                  onClick={() => handleVocabClick(opt)}
                >{opt}</Button>
              </motion.div>
            ))}
          </div>
        </Card>
      )}

      {/* Memory Test Phase */}
      {phase==='color'&&(
        <Card className="p-8 bg-white rounded-2xl shadow-xl border-4 border-pink-300">
          {showColors ? (
            <div className="flex justify-center">
              <motion.div
                key={colorSequence[userColorInput.length]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-6xl"
              >
                {colorSequence[userColorInput.length]}
              </motion.div>
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-4 justify-center">
              {shuffle([...colorSequence]).map(color => (
                <Button
                  key={color}
                  className="text-4xl"
                  onClick={() => {
                    const updated = [...userColorInput, color];
                    setUserColorInput(updated);
                    if (color === colorSequence[updated.length - 1]) {
                      setScore(s => s + 1);
                    }
                    if (updated.length === colorSequence.length) {
                      setTimeout(() => setPhase('results'), 200);
                    }
                  }}
                >
                  {color}
                </Button>
              ))}
            </div>
          )}
          <p className="mt-4 text-center text-pink-600 font-semibold">
            {showColors
              ? 'Watch the colors in order'
              : 'Now click the colors in same order'}
          </p>
        </Card>
      )}

      {phase==='memory'&&(
        <Card className="p-8 bg-white rounded-2xl shadow-xl border-4 border-blue-300">
          {showSequence ? (
            <div className="flex justify-center">
              <motion.div key={sequence[revealIndex]} initial={{opacity:0}} animate={{opacity:1}} className="text-5xl font-bold text-purple-600">
                {sequence[revealIndex]}
              </motion.div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {sequence.map((_,idx)=>(
                <Input key={idx} placeholder={`Word ${idx+1}`} value={userInputs[idx]} onChange={e=>handleInputChange(idx,e.target.value)} className="bg-gray-100 rounded-lg text-center text-lg"/>
              ))}
              <Button size="lg" className="w-full bg-blue-500 text-white rounded-full mt-4" onClick={handleMemorySubmit}>Submit Sequence</Button>
            </div>
          )}
          {showSequence ? <p className="text-center mt-4 text-blue-600 font-semibold">Memorize each word in order</p> : <p className="text-center mt-4 text-green-600 font-semibold">Enter the words in the same order</p>}
        </Card>
      )}

       {/* Results Phase */}
       {phase==='results'&&(
        <motion.div initial={{scale:0.8,opacity:0}} animate={{scale:1,opacity:1}}>
          <div id="report-card">
            <Card className="p-6 bg-white shadow-2xl space-y-6 rounded-2xl">
              <h2 className="text-3xl font-bold text-center text-purple-600">Test Report</h2>
              {/* Professional Details Grid */}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700">
                <dt className="font-bold">Child</dt><dd>{childName}</dd>
                <dt className="font-bold">Parent</dt><dd>{parentName}</dd>
                <dt className="font-bold">Contact</dt><dd>{contact}</dd>
                <dt className="font-bold">Age</dt><dd>{age}</dd>
                <dt className="font-bold">Test Type</dt><dd>{testType === 'vocab' ? 'Vocabulary Test' : testType === 'memory' ? 'Memory Test' : 'Color Sequence Memory Test'}</dd>
                <dt className="font-bold">Date</dt><dd>{new Date().toLocaleDateString()}</dd>
                <dt className="font-bold">Total Questions</dt><dd>{totalItems}</dd>
                <dt className="font-bold">Correct Answers</dt><dd>{score}</dd>
                <dt className="font-bold">Percentage</dt><dd>{percentScore}%</dd>
              </dl>
              {/* Summary and Chart */}
              <p className="mt-4 text-center text-gray-600 italic">Below is a visual comparison of your performance against the Brainies benchmark.</p>
            </Card>

            <Card className="p-6 bg-white shadow-xl rounded-2xl">
              <h3 className="text-2xl font-bold mb-4 text-center text-purple-600">Your Score vs Brainies</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top:20,right:30,left:0,bottom:5 }}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="score" fill="#a78bfa" radius={[10,10,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div className="flex justify-center space-x-4 mt-4">
            <Button className="w-1/2 bg-indigo-600 text-white rounded-full py-2" onClick={()=>setPhase('info')}>Restart</Button>
            <Button className="w-1/2 bg-green-600 text-white rounded-full py-2" onClick={handleDownloadPDF}>Download PDF</Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

